// src/pages/TermsOfService/TermsOfService.jsx
import React from "react";
import "./TermsOfService.css";
import { useNavigate } from "react-router-dom";
import useScrollToTop from "../../helpers/useScrollToTop";

function TermsOfService() {
  useScrollToTop();
  const navigate = useNavigate();

  const goBack = () => navigate(-1);

  return (
    <div className="terms-wrapper">
      <div className="terms-card">
        <h1>Terms of Service</h1>
        <p className="terms-updated">Last updated: November 19, 2025</p>

        <p className="terms-intro">
          Welcome to <strong>Little Paws Place™</strong> (“Little Paws Place,”
          “we,” “our,” or “us”). These Terms of Service (“Terms”) govern your
          use of our website, SmartTags, and related services, including any
          AI-powered features (together, the “Services”).
        </p>

        <p className="terms-note">
          By accessing or using the Services, you agree to these Terms. If you
          do not agree, please do not use the Services.
        </p>

        <h3>1. Accounts & SmartTags</h3>
        <p>
          To use certain features, including SmartTag activation and AI tools,
          you may need to create an account. You are responsible for:
        </p>
        <ul>
          <li>Keeping your login credentials secure.</li>
          <li>
            Ensuring the information you provide (including tag IDs such as
            “LP####”) is accurate and current.
          </li>
          <li>
            Not sharing your account with others or impersonating another user.
          </li>
        </ul>

        <h3>2. SmartTag & AI Usage</h3>
        <p>
          Little Paws SmartTags link a unique ID to a pet profile and related
          tools. Our AI features (“Paw AI” and other assistants) are designed to
          help you manage information about your pet and SmartTag, but:
        </p>
        <ul>
          <li>
            They do <strong>not</strong> provide medical, veterinary, or
            emergency advice.
          </li>
          <li>
            You should always consult a licensed professional for health,
            safety, or legal concerns regarding your pet.
          </li>
          <li>
            We may change, limit, or discontinue AI features at any time as we
            improve the service.
          </li>
        </ul>

        <h3>3. Subscriptions & Payments</h3>
        <p>
          Some features require an active subscription or paid plan. By starting
          a subscription, you authorize us (and our payment processors, such as
          Stripe) to charge the payment method you provide on a recurring basis
          until you cancel.
        </p>
        <ul>
          <li>
            Pricing, plans, and included features may change. We’ll post updates
            on our site when they do.
          </li>
          <li>
            You are responsible for canceling your plan if you no longer wish to
            be billed.
          </li>
          <li>
            Any refunds or credits are handled at our discretion and may be
            subject to additional terms.
          </li>
        </ul>

        <h3>4. Acceptable Use</h3>
        <p>You agree not to use the Services to:</p>
        <ul>
          <li>Violate any applicable law or regulation.</li>
          <li>Attempt to access or modify another user’s account or tag.</li>
          <li>
            Interfere with or disrupt the operation, security, or integrity of
            the Services.
          </li>
          <li>
            Upload, share, or transmit content that is illegal, abusive, or
            harmful.
          </li>
        </ul>

        <h3>5. Data & Privacy</h3>
        <p>
          We collect and use certain information in connection with operating
          the Services. For details on how we handle your data, please review
          our <a href="/privacy-policy">Privacy Policy</a>.
        </p>

        <h3>6. No Warranties</h3>
        <p>
          The Services are provided on an “as-is” and “as-available” basis. We
          do not guarantee that:
        </p>
        <ul>
          <li>The Services will be uninterrupted or error-free.</li>
          <li>
            The information provided by AI features will always be complete,
            accurate, or suitable for your specific situation.
          </li>
        </ul>

        <h3>7. Limitation of Liability</h3>
        <p>
          To the fullest extent permitted by law, Little Paws Place is not
          liable for any indirect, incidental, special, or consequential
          damages, or for any loss of data, profits, or goodwill arising from
          your use of the Services.
        </p>
        <p>
          Your use of the Services, including any decisions you make based on
          AI-generated suggestions, is at your own risk.
        </p>

        <h3>8. Third-Party Services</h3>
        <p>
          We may integrate with third-party platforms (such as payment
          processors or social networks). Your use of those services is governed
          by their own terms and policies. We are not responsible for the
          actions, content, or services of third parties.
        </p>

        <h3>9. Changes to These Terms</h3>
        <p>
          We may update these Terms from time to time. When we do, we will
          update the “Last updated” date above. Your continued use of the
          Services after changes take effect means you accept the revised Terms.
        </p>

        <h3>10. Contact</h3>
        <p>
          If you have questions about these Terms, you can reach us at{" "}
          <a href="mailto:contact@littlepawsplace.com">
            contact@littlepawsplace.com
          </a>
          .
        </p>

        <p className="terms-disclaimer">
          This summary is provided for convenience and does not replace the full
          meaning of the Terms. Consider consulting a legal professional if you
          need advice tailored to your situation.
        </p>

        <button className="terms-back-button" onClick={goBack}>
          ← Go Back
        </button>
      </div>
    </div>
  );
}

export default TermsOfService;
